import { XClose } from '@untitledui/icons'
import React, { isValidElement, useCallback, useRef, useState } from 'react'
import { useToast as useAriaToast } from 'react-aria'
import { twJoin } from 'tailwind-merge'

import useTypedTranslation from '@hooks/useTypedTranslation.ts'
import { STATE_ICONS } from '@utils/icons.ts'
import { isReactComponent } from '@utils/is-react-component.ts'

import { ToastProgressRing } from './ToastProgressRing'
import { usePauseOnInteraction } from './usePauseOnInteraction'
import { useToastTimer } from './useToastTimer'

import type { ToastContent } from '@/lib/provider/ToastContext'
import type { ToastPosition } from '@/lib/provider/ToastProvider'
import type { QueuedToast, ToastState } from '@react-stately/toast'

interface ToastProps {
  toast: QueuedToast<ToastContent>
  state: ToastState<ToastContent>
  position: ToastPosition
}

const DEFAULT_DURATIONS = {
  error: 7000,
  default: 5000,
}

/**
 * Get default duration based on variant
 */
function getDefaultDuration(variant: ToastContent['variant']): number {
  return variant === 'error' ? DEFAULT_DURATIONS.error : DEFAULT_DURATIONS.default
}

/**
 * Get animation class based on position and closing state
 */
function getAnimationClass(position: ToastPosition, isClosing: boolean): string {
  if (isClosing) {
    return 'animate-toast-fade-out'
  }

  switch (position) {
    case 'top-left':
      return 'animate-toast-slide-in-top-left'
    case 'top-center':
      return 'animate-toast-slide-in-top-center'
    case 'top-right':
      return 'animate-toast-slide-in-top-right'
    case 'bottom-left':
      return 'animate-toast-slide-in-bottom-left'
    case 'bottom-center':
      return 'animate-toast-slide-in-bottom-center'
    case 'bottom-right':
      return 'animate-toast-slide-in-bottom-right'
  }
}

const variantStyles = {
  info: {
    iconBadge: 'text-utility-brand-600',
    title: 'text-fg-primary',
    description: 'text-fg-secondary',
    actionButton: 'text-utility-brand-600 hover:text-utility-brand-700',
    dismissButton: 'text-fg-tertiary hover:text-fg-secondary',
  },
  success: {
    iconBadge: 'text-utility-success-600',
    title: 'text-fg-primary',
    description: 'text-fg-secondary',
    actionButton: 'text-utility-success-600 hover:text-utility-success-700',
    dismissButton: 'text-fg-tertiary hover:text-fg-secondary',
  },
  warning: {
    iconBadge: 'text-utility-warning-600',
    title: 'text-fg-primary',
    description: 'text-fg-secondary',
    actionButton: 'text-utility-warning-600 hover:text-utility-warning-700',
    dismissButton: 'text-fg-tertiary hover:text-fg-secondary',
  },
  error: {
    iconBadge: 'text-utility-error-600',
    title: 'text-fg-primary',
    description: 'text-fg-secondary',
    actionButton: 'text-utility-error-600 hover:text-utility-error-700',
    dismissButton: 'text-fg-tertiary hover:text-fg-secondary',
  },
  gray: {
    iconBadge: 'text-utility-gray-700',
    title: 'text-fg-primary',
    description: 'text-fg-secondary',
    actionButton: 'text-utility-gray-600 hover:text-utility-gray-700',
    dismissButton: 'text-fg-tertiary hover:text-fg-secondary',
  },
}

/**
 * Toast notification component
 * This component should not be used directly but rather through the useToast hook.
 */
export const Toast: React.FC<ToastProps> = ({ toast, state, position }) => {
  const { t } = useTypedTranslation()
  const ref = useRef<HTMLDivElement>(null)
  const { toastProps, titleProps, closeButtonProps, descriptionProps } = useAriaToast(
    { toast },
    state,
    ref
  )

  const {
    title,
    description,
    variant = 'info',
    action,
    secondaryAction,
    icon,
    hideDismissButton = false,
    duration,
    showProgressRing = true,
  } = toast.content

  const [isClosing, setIsClosing] = useState(false)
  const actualDuration = duration ?? getDefaultDuration(variant)
  const shouldShowProgressRing = showProgressRing && actualDuration > 0

  const { isPaused, interactionProps } = usePauseOnInteraction(actualDuration > 0)

  // Create a stable callback that won't cause useToastTimer to re-run
  const handleClose = useCallback(() => {
    setIsClosing(true)
  }, [])

  function handleCloseAnimationEnd() {
    if (isClosing) {
      state.close(toast.key)
    }
  }

  const progress = useToastTimer({
    duration: actualDuration,
    isPaused,
    onComplete: handleClose,
  })

  const styles = variantStyles[variant]

  return (
    <div
      {...toastProps}
      {...interactionProps}
      ref={ref}
      data-variant={variant}
      onAnimationEnd={handleCloseAnimationEnd}
      className={twJoin(
        'pointer-events-auto relative w-full',
        'bg-white dark:bg-gray-950',
        'rounded-xl border border-gray-200 dark:border-gray-800',
        'shadow-lg',
        getAnimationClass(position, isClosing)
      )}
    >
      <div className="flex items-start gap-4 p-4">
        <ToastIconBadge variant={variant} icon={icon} />

        <div className="min-w-0 flex-1">
          <div {...titleProps} className={twJoin('text-sm/5 font-semibold', styles.title)}>
            {title}
          </div>

          {description && (
            <div className={twJoin('mt-1 text-sm/5', styles.description)} {...descriptionProps}>
              {description}
            </div>
          )}

          <ToastActions
            action={action}
            secondaryAction={secondaryAction}
            hideDismissButton={hideDismissButton}
            variant={variant}
            onClose={handleClose}
          />
        </div>

        <div className="relative size-9 shrink-0">
          {shouldShowProgressRing && <ToastProgressRing progress={progress} />}

          <button
            {...closeButtonProps}
            className={twJoin(
              'relative z-10 flex size-full items-center justify-center',
              'cursor-pointer rounded-md transition-colors',
              'hover:bg-gray-50 dark:hover:bg-gray-900',
              'focus:outline-none',
              styles.dismissButton
            )}
            aria-label={t('common.toast.close')}
            onClick={handleClose}
          >
            <XClose className="size-5" />
          </button>
        </div>
      </div>
    </div>
  )
}

type ToastActionsProps = Pick<ToastContent, 'action' | 'secondaryAction' | 'hideDismissButton' | 'variant' | 'onClose'>

/**
 * Action buttons displayed at the bottom of the toast
 */
function ToastActions({
  action,
  secondaryAction,
  hideDismissButton,
  variant,
  onClose,
}: ToastActionsProps) {
  const { t } = useTypedTranslation()
  const styles = variantStyles[variant ?? 'info']

  const actionButtonClasses = twJoin(
    'cursor-pointer text-sm font-semibold',
    'hover:underline focus:underline focus:outline-none'
  )

  return (
    <div className="mt-3 flex gap-3">
      {action && (
        <button
          className={twJoin(actionButtonClasses, styles.actionButton)}
          onClick={() => {
            action.onAction()
            onClose?.()
          }}
        >
          {action.label}
        </button>
      )}
      {secondaryAction && (
        <button
          className={twJoin(actionButtonClasses, styles.dismissButton)}
          onClick={() => {
            secondaryAction.onAction()
            onClose?.()
          }}
        >
          {secondaryAction.label}
        </button>
      )}
      {!secondaryAction && !hideDismissButton && (
        <button
          className={twJoin(actionButtonClasses, styles.dismissButton)}
          onClick={onClose}
        >
          {t('common.toast.dismiss')}
        </button>
      )}
    </div>
  )
}

type ToastIconBadgeProps = Pick<ToastContent, 'variant' | 'icon'>

/**
 * Icon badge displayed on the left side of the toast
 */
function ToastIconBadge({ variant, icon: customIcon }: ToastIconBadgeProps) {
  const iconClasses = variantStyles[variant ?? 'info'].iconBadge

  const getIcon = () => {
    if (isValidElement(customIcon)) {
      return customIcon
    }

    if (isReactComponent(customIcon)) {
      const CustomIconComponent = customIcon as React.FC<{className: string}>
      return <CustomIconComponent className="size-6" />
    }

    let VariantIcon

    switch (variant) {
      case 'success':
        VariantIcon = STATE_ICONS.success
        break
      case 'warning':
        VariantIcon = STATE_ICONS.warning
        break
      case 'error':
        VariantIcon = STATE_ICONS.error
        break
      case 'gray':
      default:
        VariantIcon = STATE_ICONS.info
        break
    }

    return <VariantIcon className="size-6" />
  }

  return (
    <div
      className={twJoin(
        'size-10 shrink-0 rounded-md shadow-xs-skeumorphic',
        'flex items-center justify-center',
        iconClasses
      )}
    >
      {getIcon()}
    </div>
  )
}